import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  computed,
  OnInit,
} from '@angular/core'
import { RouterModule } from '@angular/router'
import {
  LineChart,
  SunburstChart,
  PieChart,
  HeatmapChart,
} from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent as eGridComponent,
  LegendComponent,
  GraphicComponent,
  VisualMapComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'

import { Enums, HuntCoverage } from '@cyborg-monorepo/hunter/common'
import { LinkComponent, SelectOption, TypographyComponent } from '@hyperkit'

import {
  HuntEditorService,
  HuntViewService,
  NavigationService,
} from '@hunt/services'
import { SessionService } from '@hunt/services'

import { addNewLinesBetweenWords } from '../../../../helpers/utils'
import {
  ChartUtils,
  SunburstData,
} from '../../../hunt/overview/dashboard/charts/chart-utils'
import * as noDataChartJson from '../../../hunt/overview/dashboard/charts/no-data-chart.json'
import * as sunburstChartJson from '../../../hunt/overview/dashboard/charts/sunburst-chart.json'
import * as threatPieChartJson from '../../../hunt/overview/dashboard/charts/threat-pie-chart.json'
import { HuntMetricsService } from '../../../shared/services'

echarts.use([
  TitleComponent,
  TooltipComponent,
  eGridComponent,
  LegendComponent,
  VisualMapComponent,
  LineChart,
  CanvasRenderer,
  GraphicComponent,
  SunburstChart,
  PieChart,
  HeatmapChart,
])

@Component({
  standalone: true,
  selector: 'final-report-coverage',
  imports: [RouterModule, TypographyComponent, LinkComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './final-report-coverage.component.html',
  styleUrls: ['final-report-coverage.component.scss'],
})
export class FinalReportCoverageComponent implements OnInit, AfterViewInit {
  readonly huntView = inject(HuntViewService)
  readonly huntEditor = inject(HuntEditorService)
  readonly nav = inject(NavigationService)
  readonly cdr = inject(ChangeDetectorRef)
  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  public outcomeValues: Enums.OutcomeEnum[] = Object.values(Enums.OutcomeEnum)

  public huntActorCoverage: HuntCoverage[]
  public huntMITRECoverage: HuntCoverage[]
  public huntThreatCoverage: HuntCoverage[]

  // Coverage
  public actorSunburst: echarts.ECharts
  public actorSunburstOptions = sunburstChartJson
  public mitreSunburst: echarts.ECharts
  public mitreSunburstOptions = sunburstChartJson
  public threatPie: echarts.ECharts
  public threatPieOptions = threatPieChartJson
  public noDataOptions = noDataChartJson
  public imageUrl: string
  public chartColors = ChartUtils.sunburstColors
  public hide = false

  readonly huntMetricsService = inject(HuntMetricsService)

  public ngOnInit(): void {
    this.huntMetricsService
      .getHuntThreatCoverageById(this.huntView.huntId())
      .subscribe((results) => {
        this.huntThreatCoverage = results.results
        if (this.huntThreatCoverage?.length === 0) {
          this.threatPie.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const threatChartData = results.results.map(
            ({ name, huntPackageCount }, index) => ({
              value: Number(huntPackageCount),
              name: addNewLinesBetweenWords(name),
              itemStyle: { color: this.chartColors[index] },
            }),
          )
          this.threatPieOptions.series[0].data = threatChartData
          this.threatPieOptions.series[0].name = 'Top 10 Threats'
          this.threatPie.setOption(this.threatPieOptions)
        }
        this.cdr.markForCheck()
      })

    this.huntMetricsService
      .getHuntMITRECoverageById(this.huntView.huntId())
      .subscribe((results) => {
        this.huntMITRECoverage = results.results
        if (this.huntMITRECoverage?.length === 0) {
          this.mitreSunburst.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const sunburstResults: SunburstData[] = this.createCoverageSunburst(
            results.results,
          )
          this.mitreSunburstOptions.series.data = sunburstResults
          this.mitreSunburst.setOption(this.actorSunburstOptions)
        }

        this.cdr.markForCheck()
      })

    this.huntMetricsService
      .getHuntActorCoverageById(this.huntView.huntId())
      .subscribe((results) => {
        this.huntActorCoverage = results.results

        if (this.huntActorCoverage?.length === 0) {
          this.actorSunburst.setOption(this.noDataOptions, { notMerge: true })
        } else {
          const sunburstResults: SunburstData[] = this.createCoverageSunburst(
            results.results,
          )
          this.actorSunburstOptions.series.data = sunburstResults
          this.actorSunburst.setOption(this.actorSunburstOptions)
        }
        this.cdr.markForCheck()
      })
  }

  public ngAfterViewInit(): void {
    this.mitreSunburst = echarts.init(
      document.getElementById('top-mitre-container'),
    )
    this.actorSunburst = echarts.init(
      document.getElementById('top-actors-container'),
    )
    this.threatPie = echarts.init(
      document.getElementById('top-threats-container'),
    )

    window.addEventListener('resize', () => {
      this.actorSunburst.resize()
      this.mitreSunburst.resize()
      this.threatPie.resize()
    })
  }

  onSelectionChange(selectedOption: SelectOption | string | number) {
    this.huntEditor.updateOutcome(selectedOption as Enums.OutcomeEnum)
  }

  public createCoverageSunburst(results: HuntCoverage[]): SunburstData[] {
    const sunburstData: SunburstData[] = []
    const country_set: Set<string> = new Set()
    let count = 0
    for (const result of results) {
      if (!country_set.has(result.category)) {
        const children: SunburstData[] = []
        count++
        children.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        })
        sunburstData.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.category),
          children,
        })
        country_set.add(result.category)
      } else {
        const targetCategory = sunburstData.find(
          (data) => data.name == addNewLinesBetweenWords(result.category),
        )
        targetCategory.children.push({
          itemStyle: { color: `${targetCategory.itemStyle.color}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        })
      }
    }
    return sunburstData
  }
}
